import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiError, initials, minutesAccusative, rubles, useAction } from '@limuzin/kit';
import type { PassengerRideView, PaymentView } from '@limuzin/passenger';
import { cachedOrderPhone, cachedSupportPhone, cancelRide, laterRide, order } from '../api';
import { OrderName } from '../ids';
import { leaveRating } from '../ratings';
import { askToNotify } from '../notify';
import { clock, liveRideSeconds, liveTotalKopeks, waitingChip } from '../fare';
import { deliverRating, useActiveRide, useAvatar, useNow, useSession } from '../session';
import { ridePicture, useNight } from '../daylight';
import { useDark } from '../theme';
import { Icon, type IconName } from '../material';
import { BankMark } from '../icons';
import { copyText } from '../clipboard';
import { BANKS, BANK_LABEL, type Bank, bankLink, opensBankApps } from '../banks';
import {
  Button, ConfirmSheet, EmptyCard, ErrorNote, GrowableBox, Initials, Panel, Plate, RatingStar,
  RouteDetail, Sheet, Soft, Spinner, TransferNumber, destinationOf, pickupOf, showProblem, toast,
  useErrorHaptic,
} from '../widgets';
import { BookingScreen } from './booking';
import { repeat, toStops, useDraft } from './draft';
import { type SearchReading, searchMinutes, searchReading } from './search';
import { useFact } from './facts';
import { PaperReceipt, receiptFor } from './paper-receipt';
import { type NightReading, lineIsNews, lineSeen, morningLabel, nightReading } from './night';
import { type SceneOptions, STILL, paintStreet } from './street-scene';

/**
 * The first tab. It is the order form until there is a ride and the ride until there
 * is not — a passenger never has to go looking for what is happening. A port of
 * `apps/rider/lib/features/ride/ride_screen.dart`.
 */
export function RideTab() {
  const ride = useActiveRide();
  const { api, reload, ridesKnown, ridesFailed, connection } = useSession();
  const navigate = useNavigate();
  const { set } = useDraft();
  const { adoptRide } = useSession();
  // Bumped when the night screen is answered, so the tab draws «Ищем водителя» again.
  const [, setSeen] = useState(0);
  // «Изменить заказ» asks too, and in words of its own: see `EditSheet`.
  const [asking, setAsking] = useState<'cancel' | 'edit' | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const night = useNight();
  const dark = useDark();
  // The sentence on the picture counts the Время подачи down, so the head of this
  // screen moves between server updates the way the card's clocks do — and
  // «Ищем водителя» counts the pool's minutes down the same way.
  const now = useNow(
    ride !== null && ['SEARCHING', 'EN_ROUTE', 'ON_SITE', 'IN_PROGRESS'].includes(ride.status),
  );

  if (!ride) {
    // No ride known is not the same as no ride: a list that has not arrived could be
    // hiding a car on its way, and «Новый заказ» would be the wrong thing to offer.
    if (ridesKnown) return <BookingScreen />;
    return ridesFailed ? <NotKnown /> : <Spinner />;
  }

  // The channel down with a ride under way: what is on screen is what was known when
  // it went, and the passenger is told so (ADR 0030).
  const stale = connection === 'down' && LIVE.includes(ride.status);

  // An order the server has cannot be changed, only given up: «Изменить заказ»
  // cancels it and puts its route back in the form, where the passenger corrects it
  // and orders again.
  const cancel = async () => {
    const edit = asking === 'edit';
    setCancelling(true);
    setError(null);
    try {
      await cancelRide(api, ride.id);
      if (edit) set(repeat(ride.stops));
      await reload();
      setAsking(null);
    } catch (problem) {
      if (problem instanceof ApiError && problem.code === 'offline') {
        // Said along the bottom with the number to ring, and the answer may be what
        // was lost rather than the cancel.
        setAsking(null);
        cancelNotSent(ride);
        void reload();
        return;
      }
      setError(problem instanceof ApiError ? problem : new ApiError('unknown', 'Что-то пошло не так'));
    } finally {
      setCancelling(false);
    }
  };

  // Asked over whichever screen the ride is on, and about the ride as it is now: a
  // driver who takes the order while the sheet is open changes what is given up.
  const close = () => {
    setAsking(null);
    setError(null);
  };
  const sheet =
    asking === 'edit' ? (
      <EditSheet busy={cancelling} error={error} onConfirm={() => void cancel()} onClose={close} />
    ) : (
      asking === 'cancel' && (
        <CancelSheet
          ride={ride}
          busy={cancelling}
          error={error}
          onConfirm={() => void cancel()}
          onClose={close}
        />
      )
    );
  const ask = (what: 'cancel' | 'edit' = 'cancel') => {
    setError(null);
    setAsking(what);
  };

  // «Заказать на утро»: the order given up for a предзаказ in one request the server
  // has to take (ADR 0030). The tab goes back to the form, which a placed предзаказ
  // leaves empty, and says where the order went.
  const later = async (at: Date) => {
    setCancelling(true);
    setError(null);
    try {
      const next = await laterRide(api, ride.id, at);
      adoptRide(next);
      set({ from: '', stops: [''], comment: '', when: 'now' });
      await reload();
      toast(`Предзаказ на ${morningLabel(at, new Date()).toLowerCase()} принят. Он в «Поездках»`);
    } catch (problem) {
      setError(problem instanceof ApiError ? problem : new ApiError('unknown', 'Что-то пошло не так'));
    } finally {
      setCancelling(false);
    }
  };

  if (ride.status === 'SEARCHING') {
    // Nobody, or nobody free, on the line: said first, before the ordinary search,
    // and once — again only if it gets worse.
    const night = nightReading(ride, new Date(now));
    if (night && lineIsNews(night.line, lineSeen.get(ride.id))) {
      return (
        <>
          <NightScreen
            reading={night}
            now={new Date(now)}
            busy={cancelling}
            error={asking ? null : error}
            onContinue={() => {
              lineSeen.set(ride.id, night.line);
              setSeen((n) => n + 1);
            }}
            onMorning={() => void later(night.morning!)}
            onCancel={() => ask('cancel')}
          />
          {sheet}
        </>
      );
    }
    return (
      <>
        <SearchingScreen
          ride={ride}
          stale={stale}
          reading={searchReading(ride, now)}
          now={now}
          night={night}
          busy={cancelling}
          onEdit={() => ask('edit')}
          onCancel={() => ask('cancel')}
        />
        {sheet}
      </>
    );
  }
  if (ride.status === 'EXPIRED') return <NobodyTook ride={ride} />;

  const picture = pictureFor(ride);
  const finished = ride.status === 'COMPLETED';

  const body = (
    <>
      {stale && <StaleNote />}
      {/* The state and the sentence are the head of the card — except where a
          picture of the street carries them instead, and the card then starts at
          whatever is left. */}
      <ActiveRideCard
        ride={ride}
        now={now}
        heading={picture === null}
        onOpenChat={() => navigate(`/chat/${ride.id}`)}
      />
      <ErrorNote error={error} />
      {finished && (
        <div style={{ marginTop: picture === null ? 16 : 0 }}>
          <Summary ride={ride} />
        </div>
      )}
    </>
  );

  // The way out, standing at the foot of the screen with whatever is left between.
  const foot = !finished && ride.canCancel && (
    <Button tone="stop" size="foot" busy={cancelling} onClick={() => ask()}>
      Отменить поездку
    </Button>
  );

  if (picture === null) {
    return (
      <div className="screen-scroll">
        <div className="column ride-list">
          {body}
          {foot && <div style={{ marginTop: 24 }}>{foot}</div>}
        </div>
        {sheet}
      </div>
    );
  }

  return (
    <div className="street">
      <div className="street-picture">
        {/* The words on it say everything the picture says. */}
        <img src={ridePicture(picture.name, night, dark)} alt="" />
        {picture.name === 'zavershen' && (
          <PaperReceipt lines={receiptFor(ride)} night={night} dark={dark} />
        )}
        <div className="street-dusk">
          <div className="street-label" style={{ color: picture.ink }}>
            {statusLabel(ride)}
          </div>
          <div className="street-title">{lead(ride, now)}</div>
        </div>
        <div className="street-fade" />
      </div>
      {/* The ride scrolls inside the panel rather than the street scrolling away
          with it: the picture is the ground of this screen, and the state and the
          sentence stay on the glass while the car and the driver are read under
          them. */}
      <div className="street-panel">
        <div className="street-inner">
          {body}
          {foot && (
            <>
              <div className="foot-spacer" style={{ minHeight: 16 }} />
              {foot}
            </>
          )}
        </div>
      </div>
      {sheet}
    </div>
  );
}

/**
 * Asked once before a ride is cancelled, wherever the passenger cancels it: the foot
 * of the ride tab, «Ищем водителя», and a предзаказ in «Мои поездки». A cancelled ride
 * cannot be taken back — the order has to be placed again, and a driver already on
 * the way has been turned round. `confirmCancel` in the phone app.
 *
 * The question names what is being given up at the state the ride is in, and the way
 * out says «Не отменять»: under «Отменить поездку», «Отмена» would read as a second
 * way to cancel.
 */
export function CancelSheet({
  ride,
  busy,
  error,
  onConfirm,
  onClose,
}: {
  ride: PassengerRideView;
  busy: boolean;
  error: ApiError | null;
  onConfirm: () => void;
  onClose: () => void;
}) {
  const [title, hint, label] = cancelQuestion(ride);
  return (
    <ConfirmSheet
      title={title}
      hint={hint}
      label={label}
      icon="close"
      keep="Не отменять"
      busy={busy}
      error={error}
      onConfirm={onConfirm}
      onClose={onClose}
    />
  );
}

/**
 * Asked before «Изменить заказ» on «Ищем водителя» — `confirmEdit` in the phone app.
 * An order the server has cannot be changed, only given up and placed again, and the
 * passenger should know the search starts over rather than find out.
 */
function EditSheet({
  busy,
  error,
  onConfirm,
  onClose,
}: {
  busy: boolean;
  error: ApiError | null;
  onConfirm: () => void;
  onClose: () => void;
}) {
  return (
    <ConfirmSheet
      title="Изменить заказ?"
      hint="Этот заказ отменим, а маршрут останется в форме — поправьте его и закажите снова. Поиск водителя начнётся заново."
      label="Изменить заказ"
      icon="edit_outlined"
      keep="Не менять"
      busy={busy}
      error={error}
      onConfirm={onConfirm}
      onClose={onClose}
    />
  );
}

function cancelQuestion(ride: PassengerRideView): [string, string, string] {
  switch (ride.status) {
    case 'SCHEDULED':
      return ['Отменить предзаказ?', 'Машина к назначенному времени не приедет.', 'Отменить предзаказ'];
    case 'SEARCHING':
      return [
        'Отменить заказ?',
        'Мы перестанем искать водителя. Чтобы поехать, заказ придётся оформить заново.',
        'Отменить заказ',
      ];
    case 'ON_SITE':
      return ['Отменить поездку?', 'Водитель уже на месте и ждёт вас.', 'Отменить поездку'];
    default:
      return ['Отменить поездку?', 'Водитель уже едет к вам.', 'Отменить поездку'];
  }
}

/**
 * Which street a state stands on and what its name is written in there, or null for
 * the states that stand on none — `_pictureFor` in the phone app.
 *
 * «Заказ принят» is the car arriving, the whole of what this app can honestly show of
 * a car it is not tracking (ADR 0005); «На месте» the same street with the car at the
 * kerb; «В пути» the road out of town; the stop the car pulled over at a shop, asked
 * first because the ride is «В пути» underneath it; «Вы на месте» the far end. The
 * ink is the state's own: pale blue while the car is coming, amber once it is here,
 * green under way, amber again on a paid wait, violet once it is over.
 */
function pictureFor(ride: PassengerRideView): { name: string; ink: string } | null {
  if (standing(ride)) return { name: 'stop', ink: '#FFD07A' };
  switch (ride.status) {
    case 'ACCEPTED':
    case 'EN_ROUTE':
      return { name: 'podacha', ink: '#A8D4FF' };
    case 'ON_SITE':
      return { name: 'na_meste', ink: '#FFD07A' };
    case 'IN_PROGRESS':
      return { name: 'v_puti', ink: '#8DE8B4' };
    case 'COMPLETED':
      return { name: 'zavershen', ink: '#CBBDFF' };
    default:
      return null;
  }
}

/**
 * «Ищем водителя» — `searching_screen.dart` in the phone app. It stands on a street
 * like every ride screen after it: the passenger at the kerb with the order in hand
 * and the town's cars going past (see `street-scene.ts`), the stage written into the
 * head of it, and the rest of the search on the panel under it — the time it has
 * left once there is such a thing, the order itself, so the passenger sees it was
 * placed, and the two ways out.
 *
 * The same street and the same share of the screen as «Заказ принят», so that a
 * driver taking the order changes the picture and the panel's contents, and nothing
 * moves.
 */
function SearchingScreen({
  ride,
  stale,
  reading,
  now,
  night,
  busy,
  onEdit,
  onCancel,
}: {
  ride: PassengerRideView;
  /** The channel is down: what the screen shows is what was known when it went. */
  stale: boolean;
  reading: SearchReading;
  /** The instant `reading` was taken at, which the facts take turns by. */
  now: number;
  /** Who is on the line, once the night screen has been answered: its `note` stays. */
  night: NightReading | null;
  busy: boolean;
  onEdit: () => void;
  onCancel: () => void;
}) {
  const pooled = reading.stage !== 'offering';
  const last = reading.stage === 'lastMinute';
  const afterDark = useNight();
  const dark = useDark();
  return (
    <div className={`street searching-street${last ? ' last' : ''}`}>
      <div className="street-picture">
        <SearchStreet
          sky={afterDark ? 'night' : dark ? 'dark' : 'day'}
          stage={reading.stage}
          left={reading.left}
        />
        <div className="street-dusk">
          {/* Before the pool the stage says «Ищем водителя» itself, and the line
              over it says what is already true: the order is with the server. */}
          <div className="street-label" style={{ color: last ? '#FFD07A' : '#A8D4FF' }}>
            {pooled ? 'Ищем водителя' : 'Заказ отправлен'}
          </div>
          <div key={reading.stage} className="street-title searching-stage">
            {reading.title}
          </div>
        </div>
        <div className="street-fade" />
      </div>
      <div className="street-panel">
        <div className="street-inner">
          {stale && <StaleNote />}
          <SearchLead reading={reading} now={now} />
          {night && (
            <div className="searching-note">
              <Icon name={night.night ? 'nightlight_round' : 'schedule_rounded'} size={16} />
              <span>{night.note}</span>
            </div>
          )}
          {reading.share !== null && (
            <div className="time-left">
              <div
                className="time-left-bar"
                role="progressbar"
                aria-label={reading.barLabel ?? undefined}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={Math.round(reading.share * 100)}
              >
                <i style={{ width: `${reading.share * 100}%` }} />
              </div>
              <div className="time-left-label">{reading.barLabel}</div>
            </div>
          )}
          <Panel radius={16} className="searching-order">
            <SearchPoint icon="local_taxi_rounded" label="Подача" address={pickupOf(ride.stops)} />
            <SearchPoint icon="place_rounded" label="Куда" address={destinationOf(ride.stops)} />
          </Panel>
          <div className="foot-spacer" style={{ minHeight: 16 }} />
          <Button tone="tint" size="foot" disabled={busy} onClick={onEdit}>
            Изменить заказ
          </Button>
          <Button tone="stop" size="foot" disabled={busy} onClick={onCancel} style={{ marginTop: 10 }}>
            Отменить заказ
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * The sentence under the stage and, in turns with it, a fact from «Знаете ли вы?»
 * (`useFact`), under the rubric it is from. The box glides to the height of
 * whichever is showing, since the two are seldom the same number of lines.
 */
function SearchLead({ reading, now }: { reading: SearchReading; now: number }) {
  const shown = useFact(reading.stage, now);
  const inner = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number | null>(null);
  useLayoutEffect(() => {
    const box = inner.current;
    if (!box || typeof ResizeObserver !== 'function') return;
    const observer = new ResizeObserver(() => setHeight(box.offsetHeight));
    observer.observe(box);
    return () => observer.disconnect();
  }, []);
  return (
    <div className="search-lead-box" style={height === null ? undefined : { height }}>
      <div ref={inner}>
        {shown ? (
          <div key={`${reading.stage}-${shown.slot}`} className="searching-stage">
            <div className="label search-fact-head">Знаете ли вы?</div>
            <p className="search-lead">{shown.fact}</p>
          </div>
        ) : (
          <p key={reading.stage} className="search-lead searching-stage">
            {reading.lead}
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * The street, moving. Drawn on a canvas at the screen's own pixels and no more often
 * than thirty times a second — it is on screen for as long as the search runs, and
 * that can be ten minutes of a phone in a hand. Anybody who has asked for no
 * animations gets one still frame, caught mid-gesture.
 *
 * A new stage is a new scene, and it fades in over the last frame of the old one
 * rather than cutting to it: the passenger sitting down should not look like a jump.
 */
function SearchStreet({
  sky,
  stage,
  left,
}: Omit<SceneOptions, 'end'> & {
  /** What is left of the search, in milliseconds — `SearchReading.left`. */
  left: number | null;
}) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const before = useRef<HTMLCanvasElement>(null);
  const started = useRef(performance.now());
  const shown = useRef(stage);
  const repaint = useRef<() => void>(undefined);
  // The street's own second at which the search gives up, so that the minute runs
  // down with every frame between the readings, which come once a second. Taken
  // again only when a reading disagrees with it by half a second — a still street,
  // or a tab back from the background — so that a car never drops out of the road on
  // a reading's jitter.
  const end = useRef<number | null>(null);
  const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (left === null) {
    end.current = null;
  } else {
    const t = still ? STILL : STILL + (performance.now() - started.current) / 1000;
    const next = t + left / 1000;
    if (end.current === null || Math.abs(next - end.current) > 0.5) end.current = next;
  }
  // A still street has no frames of its own: each reading is one.
  useEffect(() => {
    if (still) repaint.current?.();
  }, [left, still]);
  useEffect(() => {
    const element = canvas.current;
    const ctx = element?.getContext('2d');
    if (!element || !ctx) return;
    const still = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (shown.current !== stage) {
      shown.current = stage;
      // What was on the glass stays under the new scene while it fades in.
      const under = before.current;
      if (under && !still.matches && element.width > 0) {
        under.width = element.width;
        under.height = element.height;
        under.getContext('2d')?.drawImage(element, 0, 0);
        element.classList.remove('arriving');
        void element.offsetWidth;
        element.classList.add('arriving');
      }
    }
    let frame = 0;
    let drawn = -Infinity;
    const draw = (now: number) => {
      if (!still.matches) frame = requestAnimationFrame(draw);
      if (now - drawn < 32) return;
      drawn = now;
      const { width, height } = element.getBoundingClientRect();
      const ratio = window.devicePixelRatio || 1;
      const w = Math.round(width * ratio);
      const h = Math.round(height * ratio);
      if (element.width !== w || element.height !== h) {
        element.width = w;
        element.height = h;
      }
      ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
      const t = still.matches ? STILL : STILL + (now - started.current) / 1000;
      paintStreet(ctx, width, height, t, { sky, stage, end: end.current });
    };
    const again = () => {
      cancelAnimationFrame(frame);
      drawn = -Infinity;
      frame = requestAnimationFrame(draw);
    };
    repaint.current = again;
    const resized = new ResizeObserver(again);
    resized.observe(element);
    still.addEventListener('change', again);
    return () => {
      cancelAnimationFrame(frame);
      resized.disconnect();
      still.removeEventListener('change', again);
    };
  }, [sky, stage]);
  // The words on it say everything the picture says.
  return (
    <>
      <canvas ref={before} className="street-canvas" aria-hidden="true" />
      <canvas ref={canvas} className="street-canvas" aria-hidden="true" />
    </>
  );
}

/**
 * «Сейчас мало водителей» — `NightScreen` in the phone app. Put in front of the
 * passenger the moment the order is placed, not after the rounds have run: at twenty
 * past three with nobody on the line they should know straight away, and choose. The
 * order is looking the whole time; «Продолжить поиск» only puts «Ищем водителя» back.
 */
function NightScreen({
  reading,
  now,
  busy,
  error,
  onContinue,
  onMorning,
  onCancel,
}: {
  reading: NightReading;
  now: Date;
  busy: boolean;
  error: ApiError | null;
  onContinue: () => void;
  onMorning: () => void;
  onCancel: () => void;
}) {
  useErrorHaptic(error?.message);
  return (
    <div className="searching night">
      <div className="searching-middle">
        <div className="night-sign" aria-hidden="true">
          <Icon name="local_taxi_rounded" size={42} />
          {reading.night && (
            <span className="night-moon">
              <Icon name="nightlight_round" size={24} />
            </span>
          )}
        </div>
        <h1 className="title" style={{ color: '#fff', fontSize: 24, margin: '28px 0 0' }}>
          {reading.title}
        </h1>
        <p className="searching-lead">{reading.lead}</p>
      </div>
      <button type="button" className="filled-on-hero" disabled={busy} onClick={onContinue}>
        Продолжить поиск
      </button>
      {reading.morning && (
        <button type="button" className="morning-on-hero" disabled={busy} onClick={onMorning}>
          {busy ? <span className="spinner" /> : <Icon name="schedule_rounded" size={22} />}
          <span>
            <b>Заказать на утро</b>
            <small>{morningLabel(reading.morning, now)}</small>
          </span>
        </button>
      )}
      <button type="button" className="quiet-on-hero dim" disabled={busy} onClick={onCancel}>
        Отменить заказ
      </button>
      {error && <p className="night-error">{error.message}</p>}
    </div>
  );
}

function SearchPoint({ icon, label, address }: { icon: IconName; label: string; address: string }) {
  return (
    <div className="searching-point">
      <Icon name={icon} size={18} />
      <div>
        <div className="searching-point-label">{label}</div>
        <div className="searching-point-address">{address}</div>
      </div>
    </div>
  );
}

/**
 * The search gave up: the ride went out to drivers one at a time and then sat in
 * «Свободные» for the pool's minutes, and nobody took it. Not a dead end with an
 * «OK» on it — the passenger is standing somewhere and still wants a car, so what
 * comes first is looking again, then changing the order, and only then leaving.
 *
 * «Повторить поиск» is an order like any other: the server has to take it, and when
 * it cannot be reached the reason is said under the button (ADR 0030).
 */
function NobodyTook({ ride }: { ride: PassengerRideView }) {
  const { api, me, dismiss, adoptRide, reload } = useSession();
  const { draft, set, sent } = useDraft();
  const minutes = searchMinutes(ride);
  // The id «Повторить поиск» sends its order under (ADR 0030): the one search asked
  // for again, however many taps it takes to get there.
  const [name] = useState(() => new OrderName());
  const again = useAction(async () => {
    const townId = me?.passenger?.townId;
    if (!townId) return;
    // The same moment «Заказать» asks whether the browser may say the car is coming.
    askToNotify();
    const id = name.idFor(ride.id);
    const next = await order(api, {
      id,
      townId,
      stops: toStops({ ...draft, ...repeat(ride.stops) }),
      ...(ride.comment ? { comment: ride.comment } : {}),
    }).catch((problem: unknown) => {
      // As on «Заказать»: the answer may be what was lost, and the list is asked
      // behind the error. An order that did arrive takes the tab by itself.
      if (problem instanceof ApiError && problem.code === 'offline') void reload();
      throw problem;
    });
    name.done();
    set(repeat(ride.stops));
    adoptRide(next);
    sent(next.id);
    dismiss(ride.id);
  });

  return (
    <div className="screen-scroll">
      <div className="column ride-list">
        <Panel hero>
          <h2 className="heading">Пока не удалось найти водителя</h2>
          <p className="body" style={{ margin: '8px 0 0' }}>
            {minutes === null
              ? 'Никто из водителей не смог принять поездку.'
              : `За ${minutesAccusative(minutes)} никто из водителей не смог принять поездку.`}
          </p>
          {/* Drawn as an opened card in «Поездки» draws it, so the order being looked
              for again reads the same as it will there. */}
          <div style={{ marginTop: 16 }}>
            <RouteDetail stops={ride.stops} />
          </div>
        </Panel>
        <ErrorNote error={again.error} />
        <div className="stack" style={{ marginTop: 16, gap: 10 }}>
          <Button busy={again.pending} onClick={() => void again.run()}>
            Повторить поиск
          </Button>
          <Button
            tone="tint"
            size="foot"
            disabled={again.pending}
            onClick={() => {
              set(repeat(ride.stops));
              dismiss(ride.id);
            }}
          >
            Изменить заказ
          </Button>
          <Button tone="quiet" size="foot" disabled={again.pending} onClick={() => dismiss(ride.id)}>
            Закрыть
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * The card that stands in for the map.
 *
 * A passenger with no live position gets three things instead: what state the ride is
 * in, one sentence about what happens next, and — once the car is here — a clock that
 * says whether waiting is still free (ADR 0005).
 *
 * [heading] false is the ride tab with a picture over it, which carries the state and
 * the sentence; what was under them stays — the waiting clock at «На месте», the meter
 * at «В пути» — each as a card of its own. Where nothing is left, the ride starts at
 * «ВОДИТЕЛЬ», and at «Вы на месте», where the car and the driver have gone too, the card
 * draws nothing at all.
 */
export function ActiveRideCard({
  ride,
  now,
  heading = true,
  onOpenChat,
}: {
  ride: PassengerRideView;
  now: number;
  heading?: boolean;
  onOpenChat?: () => void;
}) {
  // The car and the driver, until the ride is over: there is no kerb left to
  // recognise a car at and nobody left to ring.
  const driver = ride.status === 'COMPLETED' ? null : ride.driver;
  const face = useAvatar(driver?.avatarKey);

  // The wait at the kerb, free and then paid. A stop on the way is paid too, but
  // the passenger is in the shop with nothing to shorten; the street says «ПЛАТНОЕ
  // ОЖИДАНИЕ», and the seconds go into «Стоимость», which goes on moving.
  const chip = ride.waiting && ride.status === 'ON_SITE' ? waitingChip(ride.waiting, now) : null;

  const waiting = chip && (
    <MeterRow
      label={chip.label}
      value={clock(chip.seconds)}
      colour={chip.paid ? 'var(--stop-text)' : 'var(--wait-text)'}
      under={heading}
    />
  );
  const meter = ride.status === 'IN_PROGRESS' && (
    <>
      <MeterRow label="Время в пути" value={clock(liveRideSeconds(ride.fare, now))} under={heading} />
      {!heading && <div style={{ height: 10 }} />}
      <MeterRow
        label="Стоимость"
        value={rubles(liveTotalKopeks(ride.fare, now))}
        hint={ride.fare.setByDriver ? 'фиксированная цена' : undefined}
        under={heading}
      />
    </>
  );

  const above: ReactNode[] = [];
  if (heading) {
    above.push(
      <Panel key="head">
        <div className="row">
          <span className="ride-dot" />
          <span className="grow ride-state">{statusLabel(ride)}</span>
        </div>
        <p className="ride-lead">{lead(ride, now)}</p>
        {waiting}
        {meter}
      </Panel>,
    );
  } else {
    // Under a picture the wait is a card of its own, in amber while the minutes
    // are free and red from the minute they cost money.
    if (waiting) {
      above.push(
        <Panel
          key="wait"
          edge={chip!.paid ? 'var(--stop-border)' : 'var(--wait-border)'}
          style={{ background: chip!.paid ? 'var(--stop-card)' : 'var(--wait-card)' }}
        >
          {waiting}
        </Panel>,
      );
    }
    if (meter) above.push(<Panel key="meter">{meter}</Panel>);
  }

  if (above.length === 0 && !driver) return null;

  return (
    <div className="stack">
      {above}
      {driver && (
        <>
          {/* Each stage opens its cards as it means to, whatever the stage before
              left folded; the key is what starts them again. */}
          <Fold key={`driver-${ride.status}`} label="Водитель" initiallyOpen dot={ride.unread > 0}>
            <div className="row" style={{ marginTop: 4, gap: 12 }}>
              <Initials text={initials(driver.name)} size={46} src={face} />
              <div className="grow">
                <div className="strong">{driver.name}</div>
                {/* The star «Профиль» gives the passenger's own rating, and the
                    figure in the body grey: this is what a passenger reads to
                    decide about the driver. */}
                <div className="row" style={{ gap: 3, marginTop: 3 }}>
                  <Icon name="star" size={13} style={{ color: 'var(--destination)' }} />
                  <span className="caption" style={{ color: 'var(--body)', fontSize: 12 }}>
                    {driver.rating.toFixed(1)}
                  </span>
                </div>
              </div>
            </div>
            <div className="row" style={{ marginTop: 12, gap: 8 }}>
              <Soft
                wide
                icon="chat_bubble_outline"
                dot={ride.unread > 0}
                onClick={onOpenChat}
                aria-label={ride.unread > 0 ? 'Чат, есть непрочитанные' : undefined}
              >
                Чат
              </Soft>
              {/* `tel:` opens the dialler on a phone and the calling app on a
                  desktop, which is the best a browser can do and enough. */}
              <a className="soft go wide" href={`tel:${driver.phone}`}>
                <Icon name="call_outlined" size={16} />
                Звонок
              </a>
            </div>
          </Fold>

          {/* Open at «На месте», where the car stands at the kerb to be found by its
              colour and plate; folded while it is on its way and once the passenger
              is in it. */}
          <Fold key={`car-${ride.status}`} label="Машина" initiallyOpen={ride.status === 'ON_SITE'}>
            {/* The car, its colour, its plate and the driver's name are what a
                passenger at the kerb looks for, so they are set in ink. */}
            <div className="ride-car">{driver.model ?? 'Машина уточняется'}</div>
            {(driver.colour || driver.plate) && (
              <>
                <div className="hairline" style={{ margin: '12px 0' }} />
                <div className="ride-details">
                  {driver.colour && (
                    <div className="grow">
                      <div className="label">Цвет</div>
                      <div className="ride-detail-value">
                        <span className="ride-car" style={{ margin: 0 }}>
                          {driver.colour}
                        </span>
                      </div>
                    </div>
                  )}
                  {driver.colour && driver.plate && <span className="ride-details-rule" />}
                  {driver.plate && (
                    <div className="grow">
                      <div className="label">Номер</div>
                      <div className="ride-detail-value">
                        <Plate plate={driver.plate} />
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </Fold>
          {/* Paying on the way, once the passenger is aboard and before the end: the
              number and the banks of «К оплате», with no figure yet. */}
          {ride.status === 'IN_PROGRESS' && driver.transferPhone && (
            <PayAhead phone={driver.transferPhone} />
          )}
        </>
      )}
    </div>
  );
}

function MeterRow({
  label,
  value,
  hint,
  colour,
  under,
}: {
  label: string;
  value: string;
  hint?: string;
  colour?: string;
  /** Whether the head of the card is over this row — what the hairline parts it
   * from and what it stands in to clear. */
  under: boolean;
}) {
  const indent = under ? 18 : 0;
  return (
    <>
      {under && <div className="hairline" style={{ marginLeft: 18, marginTop: 12 }} />}
      <div className="row" style={{ marginLeft: indent, marginTop: under ? 12 : 0 }}>
        <span className="grow">
          <span className="ride-meter-label" style={{ color: colour ?? 'var(--body)' }}>
            {label}
          </span>
          {hint && <span className="caption" style={{ display: 'block', fontSize: 12 }}>{hint}</span>}
        </span>
        <span className="numeral tabular" style={{ fontSize: 17, color: colour ?? 'var(--ink)' }}>
          {value}
        </span>
      </div>
    </>
  );
}

/**
 * The car is standing still on the way — pulled over at a stop, with the driver's
 * clock tapped over to «Ожидание». A port of `PassengerRide.isStanding`: there is no
 * status for it, so what says so is the open waiting period the driver's tap opened.
 */
export function standing(ride: PassengerRideView): boolean {
  return ride.status === 'IN_PROGRESS' && ride.fare.waitingSince !== null;
}

/**
 * The state, in the words the passenger reads for it: the server's own label, except
 * for the stop, which has no status of its own — the passenger reads «Платное
 * ожидание», because on this side of the car the money is the half they cannot see.
 */
export function statusLabel(ride: PassengerRideView): string {
  return standing(ride) ? 'Платное ожидание' : ride.statusLabel;
}

/**
 * How long the ride took, from the веха the driver marked on boarding to the one they
 * marked on arriving. Null where either is missing. Rounded to the nearest minute, and
 * up from nothing to one. A port of `_tookMinutes` in `active_ride.dart`.
 */
export function took(ride: PassengerRideView): number | null {
  const marked = (kind: string) => ride.feed.find((entry) => entry.kind === kind)?.at ?? null;
  const boarded = marked('STARTED');
  const arrived = marked('FINISHED');
  if (!boarded || !arrived) return null;
  const seconds = Math.floor((Date.parse(arrived) - Date.parse(boarded)) / 1000);
  if (seconds <= 0) return null;
  return Math.max(1, Math.floor((seconds + 30) / 60));
}

/**
 * What «Заказ принят» says while the car is on its way: the promise, counted down a
 * minute at a time — rounded up, so the last minute reads «1 минуту» and lateness
 * begins at 0, where the driver's own clock turns over to «Задержка» — and when it
 * runs out, that the car is still coming. Never more than was promised: a `now` a
 * moment behind the server's would otherwise read a whole minute over it.
 */
function coming(ride: PassengerRideView, now: number): string {
  if (ride.promiseMinutes === null) return 'Водитель выехал к вам';
  const due = ride.promiseAt === null ? null : Date.parse(ride.promiseAt);
  const left = due === null
    ? ride.promiseMinutes
    : Math.min(ride.promiseMinutes, Math.max(0, Math.ceil((due - now) / 60_000)));
  return left > 0
    ? `Машина будет примерно через ${minutesAccusative(left)}`
    : 'Машина задерживается, но скоро будет';
}

/**
 * One sentence about what is happening, in the passenger's own terms — written into
 * the picture where there is one. A port of `leadFor` in `active_ride.dart`.
 */
export function lead(ride: PassengerRideView, now: number = Date.now()): string {
  switch (ride.status) {
    case 'SCHEDULED':
      return 'Предзаказ принят. Машину начнём искать заранее';
    case 'SEARCHING':
      return 'Ищем свободную машину';
    // Only a предзаказ waits here, and its time was agreed when it was placed.
    case 'ACCEPTED':
      return 'Водитель принял заказ';
    case 'EN_ROUTE':
      return coming(ride, now);
    case 'ON_SITE':
      return 'Машина приехала и ждёт вас';
    // Where the car is standing is not something this app is told, so the sentence
    // says only that the ride has paused on the way.
    case 'IN_PROGRESS':
      return standing(ride) ? 'Остановка по пути' : `Едем до «${destinationOf(ride.stops)}»`;
    case 'COMPLETED': {
      const minutes = took(ride);
      return minutes === null ? 'Поездка завершена' : `Поездка заняла ${minutesAccusative(minutes)}`;
    }
    case 'EXPIRED':
      return 'Свободных машин не нашлось';
    // «Отменена», like the server's own label: nothing was charged, and that is the
    // only part of a неявка the passenger is told about.
    default:
      return 'Поездка отменена';
  }
}

const RATING_LABELS = [
  'Коснитесь звезды',
  'Расскажите, что пошло не так',
  'Есть над чем поработать',
  'Нормально доехали',
  'Хорошая поездка',
  'Отличная поездка — спасибо!',
];

/**
 * What the passenger sees when the ride is over: what to pay, and how it went.
 *
 * There is no «Оценить». The stars are set by tapping them and the comment is typed
 * under them, and both go when the passenger says they are done with the screen —
 * «Готово», which is also the only moment the comment is finished being written.
 */
function Summary({ ride }: { ride: PassengerRideView }) {
  const { me, dismiss } = useSession();
  const [stars, setStars] = useState(0);
  const [comment, setComment] = useState('');
  const [finishing, setFinishing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // A rating turned away is worth one retry and no more: a «Готово» that refuses to
  // close is worse than a rating that is lost.
  const [tried, setTried] = useState(false);

  const finish = async () => {
    if (finishing) return;
    if (stars > 0) {
      setFinishing(true);
      setError(null);
      // Sent now, or kept on this device and sent once there is a connection
      // (ADR 0030). A rating already left counts as sent: each seat rates once.
      const left = await leaveRating(
        {
          rideId: ride.id,
          passengerId: me?.id ?? '',
          stars,
          comment: comment.trim(),
          at: new Date().toISOString(),
        },
        deliverRating,
      );
      if (left.kind === 'refused' && !tried) {
        setFinishing(false);
        setTried(true);
        setError(left.message);
        return;
      }
      if (left.kind === 'kept') toast(RATING_KEPT);
    }
    dismiss(ride.id);
  };

  return (
    <div>
      {/* What is owed comes first: the passenger is beside a driver waiting to be
          paid, and this card is the one thing on the screen they have to do. The
          stars are a kindness, and wait under it. */}
      {ride.payment && (
        <div style={{ marginBottom: 16 }}>
          <ToPay payment={ride.payment} />
        </div>
      )}
      <Panel hero>
        <div style={{ textAlign: 'center' }}>
          <h2 className="heading">Как прошла поездка?</h2>
          <div className="stars">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                type="button"
                className={star <= stars ? 'star on' : 'star'}
                aria-label={`${star}`}
                aria-pressed={star <= stars}
                onClick={() => setStars(star)}
              >
                <RatingStar size={20} />
              </button>
            ))}
          </div>
          <p className="caption" style={{ margin: '16px 0 0', color: 'var(--muted-warm)', fontSize: 13.5 }}>
            {RATING_LABELS[stars]}
          </p>
        </div>
        {/* Two rows and a corner to pull, the way «Комментарий водителю» opens up:
            how much there is to say about a ride is the passenger's business. */}
        <div className="rating-comment">
          <GrowableBox
            placeholder="Комментарий — по желанию"
            value={comment}
            onChange={(event) => setComment(event.target.value)}
          />
        </div>
      </Panel>
      <ErrorNote error={error} />
      <div style={{ marginTop: 16 }}>
        <Button busy={finishing} onClick={() => void finish()}>
          Готово
        </Button>
      </div>
    </div>
  );
}

/**
 * «К оплате»: what the ride came to, the driver's number to transfer it to, and — on
 * Android, where a page can reach them — the bank apps that open on that transfer
 * with the number in it.
 *
 * There is no acquirer: money goes from the passenger to the driver and never through
 * the service (ADR 0004). The number is shown and copied as it always was; the bank
 * tiles are a shorter way to the same transfer, confirmed in the bank app.
 */
function ToPay({ payment }: { payment: PaymentView }) {
  const [opened, setOpened] = useState<Bank | null>(null);
  const phone = payment.transferPhone;
  const amount = rubles(payment.totalKopeks);

  // The line under the card, which speaks only when there is something to say.
  let hint: string | null = null;
  if (!phone) hint = 'Заплатите водителю наличными';
  else if (opened) {
    hint = `Сумму ${amount} введите в приложении ${BANK_LABEL[opened]}`;
  }

  return (
    <div>
      <Panel hero className="to-pay-card">
        <div className="to-pay-head">
          <h2 className="heading">К оплате</h2>
          <div className="numeral to-pay-amount">{amount}</div>
        </div>

        {phone && (
          <>
            <p className="to-pay-section">Перевод на номер телефона</p>
            <TransferNumber phone={phone} />

            {opensBankApps() && (
              <>
                <p className="to-pay-section">Через приложение банка</p>
                <BankTiles phone={phone} onOpen={setOpened} />
              </>
            )}
          </>
        )}
      </Panel>
      {hint && (
        <p className="body" style={{ margin: '10px 0 0' }}>
          {hint}
        </p>
      )}
    </div>
  );
}

/**
 * «Оплата заранее»: at «В пути», under the driver, the driver's number to transfer to
 * while the ride is still going, and — on Android — the bank apps that open on that
 * transfer, so a passenger who has paid on the way gets out at the door without
 * settling up there.
 *
 * It is «К оплате» without the figure. The meter is still running, so no amount is
 * handed to a bank either: the passenger types what they pay, and the ride's end still
 * says what it came to (ADR 0004).
 */
function PayAhead({ phone }: { phone: string }) {
  return (
    <Fold label="Оплата заранее" className="pay-ahead">
      <p className="caption pay-ahead-lead">Можно перевести водителю, не дожидаясь конца поездки</p>
      <p className="to-pay-section">Перевод на номер телефона</p>
      <TransferNumber phone={phone} />
      {opensBankApps() && (
        <>
          <p className="to-pay-section">Через приложение банка</p>
          <BankTiles phone={phone} />
        </>
      )}
    </Fold>
  );
}

/**
 * A card that folds to its label — «Водитель», «Машина», «Оплата заранее» — and opens
 * and folds on a tap anywhere along that label's row. A port of
 * `apps/rider/lib/widgets/fold_card.dart`: the ride tab is a long column, most of it
 * reached for rather than read all the way. The driver starts open, being who the
 * passenger rings or writes to; the car starts open at «На месте», where it is found
 * at the kerb; the rest start folded. Keyed by the ride's stage, each card starts
 * again as that stage means it to when the ride moves on.
 *
 * [dot] is what «Чат» inside would be wearing, shown beside the label while the card
 * is folded, so that a message is not folded away. The fold is not remembered.
 */
function Fold({
  label,
  dot = false,
  initiallyOpen = false,
  className,
  children,
}: {
  label: string;
  dot?: boolean;
  initiallyOpen?: boolean;
  className?: string;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(initiallyOpen);
  return (
    <Panel flush className={`fold ${className ?? ''}`}>
      <button type="button" className="fold-head" aria-expanded={open} onClick={() => setOpen(!open)}>
        <span className="label">{label}</span>
        {dot && !open && <span className="fold-dot pulse" aria-label="есть непрочитанные" />}
        <span className="grow" />
        <Icon name="expand_more" size={24} className={open ? 'fold-chevron open' : 'fold-chevron'} />
      </button>
      {open && <div className="fold-body">{children}</div>}
    </Panel>
  );
}

/** Room for a tile's badge and name, beside the widest name: its sides and border. */
const BANK_TILE_SIDES = 6 * 2 + 2;
const BANK_TILE_GAP = 8;
const BANK_BADGE = 28;
const MORE_BANKS = 'Ещё';

/**
 * The bank tiles of «К оплате» and «Оплата заранее», each opening its app on the
 * transfer with the number in it and the amount left to the passenger.
 *
 * A port of `apps/rider/lib/widgets/bank_row.dart`: each tile a badge with the name
 * under it, all as wide as one another and never narrower than the longest name. As
 * many as fit share the row, in their order; where they do not all fit, the last
 * place goes to «Ещё», which opens the rest in a sheet.
 */
function BankTiles({
  phone,
  onOpen,
}: {
  phone: string;
  /** Told of each bank tapped, for «К оплате» to say what is left to type. */
  onOpen?: (bank: Bank) => void;
}) {
  const row = useRef<HTMLDivElement>(null);
  const names = useRef<HTMLDivElement>(null);
  const [fit, setFit] = useState(BANKS.length);
  const [more, setMore] = useState(false);

  useLayoutEffect(() => {
    const box = row.current;
    const probe = names.current;
    if (!box || !probe) return;
    // The names are set out of sight as wide as they come, so the count follows the
    // row's width, the font arriving, and the text being zoomed alike.
    const measure = () => {
      const least = Math.max(BANK_BADGE, Math.ceil(probe.getBoundingClientRect().width)) + BANK_TILE_SIDES;
      setFit(Math.max(1, Math.floor((box.clientWidth + BANK_TILE_GAP) / (least + BANK_TILE_GAP))));
    };
    measure();
    const watch = new ResizeObserver(measure);
    watch.observe(box);
    watch.observe(probe);
    return () => watch.disconnect();
  }, []);

  const shown = BANKS.slice(0, fit >= BANKS.length ? fit : fit - 1);
  const rest = BANKS.slice(shown.length);

  const open = (bank: Bank) => {
    onOpen?.(bank);
    // A page cannot tell whether the app opened, so the number goes on the
    // clipboard too: a bank that is not there still leaves it to paste.
    void copyText(phone);
  };

  return (
    <div className="banks" ref={row}>
      <div className="bank-names" ref={names} aria-hidden="true">
        {[...BANKS.map((bank) => BANK_LABEL[bank]), MORE_BANKS].map((name) => (
          <span key={name}>{name}</span>
        ))}
      </div>
      {shown.map((bank) => (
        <a
          key={bank}
          className="bank"
          href={bankLink(bank, phone)}
          aria-label={`Открыть ${BANK_LABEL[bank]}`}
          onClick={() => open(bank)}
        >
          <BankMark bank={bank} size={BANK_BADGE} />
          <span>{BANK_LABEL[bank]}</span>
        </a>
      ))}
      {rest.length > 0 && (
        <button type="button" className="bank" aria-label="Другие банки" onClick={() => setMore(true)}>
          <span className="bank-more">
            <Icon name="more_horiz" size={20} />
          </span>
          <span>{MORE_BANKS}</span>
        </button>
      )}
      {more && (
        <Sheet title="Другие банки" onClose={() => setMore(false)}>
          <div className="stack" style={{ gap: 8 }}>
            {rest.map((bank) => (
              <a
                key={bank}
                className="bank-line"
                href={bankLink(bank, phone)}
                aria-label={`Открыть ${BANK_LABEL[bank]}`}
                onClick={() => {
                  open(bank);
                  // Not at once: a link taken out of the page before the click
                  // is done with it goes nowhere.
                  setTimeout(() => setMore(false));
                }}
              >
                <BankMark bank={bank} size={32} />
                <span>{BANK_LABEL[bank]}</span>
              </a>
            ))}
          </div>
        </Sheet>
      )}
    </div>
  );
}

/** The statuses of a ride under way, whose screen can fall behind the server's. */
const LIVE = ['SEARCHING', 'ACCEPTED', 'EN_ROUTE', 'ON_SITE', 'IN_PROGRESS'];

/** What «Готово» says as it closes over a rating with no connection to go to. */
export const RATING_KEPT = 'Оценка сохранена и отправится, когда появится связь';

/** What a cancel that did not reach the server leads with. */
export const CANCEL_NOT_SENT = 'Нет связи с сервером — отмена не дошла';

/**
 * A cancellation that did not reach the server (ADR 0030). It asks that a car stop
 * coming, and one queued until the signal returns reaches the driver as a car already
 * at the kerb — so the passenger is told it did not go and handed the phone: the
 * driver's once there is one, and support's before — a person, since the order line
 * only takes orders. `showCancelNotSent` in the phone app.
 */
export function cancelNotSent(ride: PassengerRideView): void {
  const driver = ride.driver?.phone ?? null;
  const phone = driver ?? cachedSupportPhone();
  if (phone === null) {
    showProblem(CANCEL_NOT_SENT);
    return;
  }
  showProblem(
    driver !== null
      ? `${CANCEL_NOT_SENT}. Позвоните водителю`
      : `${CANCEL_NOT_SENT}. Отменить можно по телефону`,
    { action: { label: 'Позвонить', href: `tel:${phone}` }, ms: 10_000 },
  );
}

/**
 * The line at the head of the ride while the live channel is down. Quiet, because
 * nothing is wrong with the ride — only with what this device has heard of it — and
 * it goes by itself once the channel is back. `_Stale` in the phone app.
 */
function StaleNote() {
  return (
    <div className="stale-note" role="status">
      <Icon name="cloud_off_rounded" size={18} />
      <span>Нет связи с сервером — показано, что было известно. Обновится само</span>
    </div>
  );
}

/**
 * The rides have not arrived, and nothing is known of them: not the order form, which
 * would say there is no ride, but this. The poll and the channel's return ask again by
 * themselves; «Повторить» is for the passenger who would rather not wait, and the
 * phone for one who cannot.
 */
function NotKnown() {
  const { reload } = useSession();
  const [asking, setAsking] = useState(false);
  const phone = cachedOrderPhone();
  return (
    <div className="screen-scroll">
      <div className="column ride-list">
        <EmptyCard
          title="Не удалось загрузить поездки"
          hint="Нет связи с сервером. Попробуем ещё раз, как только она появится."
        />
        <div style={{ marginTop: 16 }}>
          <Button
            busy={asking}
            onClick={() => {
              setAsking(true);
              void reload().finally(() => setAsking(false));
            }}
          >
            Повторить
          </Button>
        </div>
        {phone && (
          <a className="btn tint unreachable-phone" href={`tel:${phone}`}>
            <Icon name="phone_in_talk_rounded" size={27} />
            Сделать заказ по телефону
          </a>
        )}
      </div>
    </div>
  );
}
